import { Component, OnDestroy, ViewChild } from "@angular/core";
import { MenuSliderComponent } from "../../components/menu-slider/menu-slider.component";
import { AuthService } from "../../services/auth/auth.service";
import { AuthUser } from "../../entities/AuthUser";
import { TranslateService } from "@ngx-translate/core";
import { BrowserService } from "../../services/browser/browser.service";
import { Capacitor } from "@capacitor/core";
import { Subscription } from "rxjs";

@Component({
    selector: "app-master",
    templateUrl: "master.page.html",
    styleUrls: ["master.page.scss"]
})
export class MasterPage implements OnDestroy {
    @ViewChild("mainMenu") mainMenu: MenuSliderComponent;
    public profile = this.authService.userProfileState.getValue();
    public menuItems = [];
    public baseMenuItems = [];
    private menuState = false;
    private userProfileSubscription: Subscription;
    private languageChangeSubscription: Subscription;

    constructor(
        private authService: AuthService,
        private translateService: TranslateService,
        public browserService: BrowserService
    ) {
        this.userProfileSubscription = this.authService.userProfileState
            .asObservable()
            .subscribe((authUser: AuthUser) => {
                this.profile = authUser;
                this.getMenuItems();
            });

        // The labels themselves are rendered through the translate pipe, but
        // the order depends on the translated text, so rebuild on a language
        // change as well.
        this.languageChangeSubscription =
            this.translateService.onLangChange.subscribe(() => {
                this.getMenuItems();
            });
    }

    ngOnDestroy() {
        if (this.userProfileSubscription) {
            this.userProfileSubscription.unsubscribe();
        }

        if (this.languageChangeSubscription) {
            this.languageChangeSubscription.unsubscribe();
        }
    }

    get fleetAppLink(): string {
        if (Capacitor.getPlatform() === "ios") {
            return "https://apps.apple.com/nl/app/ab-texel-fleet/id1600435084";
        } else {
            return "https://play.google.com/store/apps/details?id=com.ab_texel.driver_app&pli=1";
        }
    }

    getMenuItems() {
        this.baseMenuItems = [
            {
                name: "navigation.profile",
                routerLink: "/profile",
                routerLinkOptions: {},
                icon: "mdi_account_circle",
                hasIf: true
            }
        ];

        this.menuItems = [
            {
                name: "navigation.events",
                routerLink: "/events",
                routerLinkOptions: {},
                icon: "cake",
                hasIf: true
            },
            {
                name: "navigation.locations-on-route",
                routerLink: "/tank-locations-car-washes",
                routerLinkOptions: {},
                icon: "mdi_local_gas_station",
                hasIf: this.hasPermission("tanklocaties-wasstraten")
            },
            {
                name: "navigation.expenses",
                routerLink: "/expenses",
                routerLinkOptions: {},
                icon: "mdi_expenses",
                hasIf: this.hasPermission("expenses")
            },
            {
                name: "navigation.colleagues",
                routerLink: "/colleagues",
                routerLinkOptions: {},
                icon: "contacts",
                hasIf: true
            },
            {
                name: "navigation.ideas",
                routerLink: "/ideas",
                routerLinkOptions: {},
                icon: "ideas",
                hasIf: true
            },
            {
                name: "navigation.furlough",
                routerLink: "/furloughs",
                routerLinkOptions: {},
                icon: "furlough",
                hasIf: this.hasPermission("furlough")
            },
            {
                name: "navigation.my-favorite-articles",
                routerLink: "/news/favorite",
                routerLinkOptions: { exact: true },
                icon: "baseline-photo-24px",
                hasIf: true
            },
            {
                name: "navigation.my-service-schedule",
                routerLink: "/content-page/1",
                routerLinkOptions: {},
                icon: "furlough",
                hasIf: this.profile?.can_access_my_service_schedule
            },
            {
                name: "navigation.fleet-app",
                routerLink: "/",
                routerLinkOptions: {},
                click: (_) => this.browserService.openLink(this.fleetAppLink),
                icon: "furlough",
                hasIf:
                    Capacitor.isNativePlatform() &&
                    this.hasPermission("fleet_app")
            },
            {
                name: "navigation.datev",
                routerLink: "/content-page/2",
                routerLinkOptions: {},
                icon: "baseline-photo-24px",
                hasIf: this.profile?.can_access_datev
            }
        ]
            .filter((item) => {
                return item.hasIf;
            })
            // localeCompare orders accented and upper case letters the way the
            // active language expects; `<` compares UTF-16 code units and
            // never reports two labels as equal.
            .sort((a, b) =>
                this.label(a.name).localeCompare(
                    this.label(b.name),
                    this.translateService.currentLang
                )
            );
    }

    /**
     * Translated label of a menu item, used to order the menu.
     *
     * The template renders the key through the translate pipe, so only the
     * ordering needs the text here.
     */
    private label(key: string): string {
        return this.translateService.instant(key);
    }

    /**
     * Permission check that tolerates the empty profile the state starts with,
     * so building the menu can never throw while the real profile is loading.
     */
    private hasPermission(permission: string): boolean {
        if (!this.profile?.business_unit_permissions) {
            return false;
        }

        return this.profile.hasPermission(permission);
    }

    onMenuStateChange() {
        if (!this.menuState) {
            this.menuState = true;
            this.mainMenu.open();
        } else {
            this.menuState = false;
            this.mainMenu.close();
        }
    }

    onMenuClosed() {
        this.menuState = false;
    }
}
